<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('campusAttendance.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('campusAttendance.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <form class="form-horizontal">
          <div class="row">
            <div class="col-md-1 form-group">
              <label></label>
              <ts-button
                outline
                class="ml-2 mt-4"
                v-tooltip="$t('campusAttendance.addManual')"
                color="info"
                @click="showForm"
              >
                {{ $t('add_new') }}
              </ts-button>
            </div>
            <div class="col-md-2 form-group">
              <label class="required">{{
                $t('campusAttendance.fromDate')
              }}</label>
              <ts-date-picker-input
                v-model="model.date_from"
                :closeOnSelect="true"
              ></ts-date-picker-input>
              <div class="validate" v-if="errors.has('date_from')">
                {{ errors.first('date_from') }}
              </div>
            </div>
            <div class="col-md-2 form-group">
              <label class="required">{{
                $t('campusAttendance.toDate')
              }}</label>
              <ts-date-picker-input
                v-model="model.date_to"
                :closeOnSelect="true"
              ></ts-date-picker-input>
              <div class="validate" v-if="errors.has('date_to')">
                {{ errors.first('date_to') }}
              </div>
            </div>
            <div class="col-md-2 form-group">
              <label>{{ $t('campusAttendance.className') }}</label>
              <treeselect
                :load-options="loadClasses"
                :options="classOptions"
                :placeholder="$t('all')"
                v-model="model.class_id"
              />
            </div>
            <div class="col-md-3 form-group">
              <label>{{ $t('campusAttendance.studentName') }}</label>
              <treeselect
                :load-options="loadStudent"
                :multiple="true"
                :limit="1"
                :options="studentOptions"
                :placeholder="$t('all')"
                v-model="model.student_id"
              />
            </div>
            <div class="col-md-2 text-right">
              <label></label>
              <ts-button
                class="mt-4 ts-mr-3"
                color="success"
                :waiting="exporting"
                @click.prevent="exportExcel"
                >{{ $t('excel') }}</ts-button
              >
              <label></label>
              <ts-button
                @click="fetchData"
                class="mt-4"
                color="primary"
                :waiting="loading"
                >{{ $t('view') }}</ts-button
              >
              <label></label>
              <ts-button
                outline
                class="ml-2 mt-4"
                v-tooltip="$t('filter')"
                @click.prevent="visible = true"
              >
                <i class="fa fa-filter"></i>
              </ts-button>
            </div>
          </div>
        </form>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <div id="table-container">
          <ts-table :records="resources" :columnLengths="18">
            <template v-slot:thead>
              <thead>
                <tr>
                  <th rowspan="2">{{ $t('campusAttendance.branch') }}</th>
                  <th rowspan="2" class="text-center">
                    {{ $t('campusAttendance.cardNumber') }}
                  </th>
                  <th rowspan="2">{{ $t('campusAttendance.studentName') }}</th>
                  <th rowspan="2" class="text-center">
                    {{ $t('campusAttendance.sex') }}
                  </th>
                  <th rowspan="2" class="text-center">
                    {{ $t('campusAttendance.datescan') }}
                  </th>
                  <th colspan="2" class="text-center">
                    {{ $t('campusAttendance.t1') }}
                  </th>
                  <th rowspan="2" class="text-center">
                    {{ $t('campusAttendance.total1') }}
                  </th>
                  <th colspan="2" class="text-center">
                    {{ $t('campusAttendance.t2') }}
                  </th>
                  <th rowspan="2" class="text-center">
                    {{ $t('campusAttendance.total2') }}
                  </th>
                  <th colspan="2" class="text-center">
                    {{ $t('campusAttendance.t3') }}
                  </th>
                  <th rowspan="2" class="text-center">
                    {{ $t('campusAttendance.total3') }}
                  </th>
                  <th colspan="2" class="text-center">
                    {{ $t('campusAttendance.t4') }}
                  </th>
                  <th rowspan="2" class="text-center">
                    {{ $t('campusAttendance.total4') }}
                  </th>
                  <th rowspan="2">{{ $t('actions') }}</th>
                </tr>
                <tr>
                  <th>{{ $t('campusAttendance.checkin') }}</th>
                  <th>{{ $t('campusAttendance.checkout') }}</th>
                  <th>{{ $t('campusAttendance.checkin') }}</th>
                  <th>{{ $t('campusAttendance.checkout') }}</th>
                  <th>{{ $t('campusAttendance.checkin') }}</th>
                  <th>{{ $t('campusAttendance.checkout') }}</th>
                  <th>{{ $t('campusAttendance.checkin') }}</th>
                  <th>{{ $t('campusAttendance.checkout') }}</th>
                </tr>
                <TableProgressBar v-show="loading" :columnLength="18" />
              </thead>
            </template>
            <template v-slot="{ record }">
              <td class="ts-whitespace-no-wrap">
                {{ record.branch.branch_name_en }}
              </td>
              <td class="ts-whitespace-no-wrap text-center">
                {{ record.card_number }}
              </td>
              <td class="ts-whitespace-no-wrap">
                {{ record.student ? record.student.full_name_en : '' }}
              </td>
              <td class="ts-whitespace-no-wrap text-center">
                {{ record.student ? record.student.gender : '' }}
              </td>
              <td class="ts-whitespace-no-wrap text-center">
                {{ record.datescan }}
              </td>
              <td class="ts-whitespace-no-wrap text-center">{{ record.t1 }}</td>
              <td class="ts-whitespace-no-wrap text-center">{{ record.t2 }}</td>
              <td class="ts-whitespace-no-wrap text-center">
                {{ record.total_hour1 }}
              </td>
              <td class="ts-whitespace-no-wrap text-center">{{ record.t3 }}</td>
              <td class="ts-whitespace-no-wrap text-center">{{ record.t4 }}</td>
              <td class="ts-whitespace-no-wrap text-center">
                {{ record.total_hour2 }}
              </td>
              <td class="ts-whitespace-no-wrap text-center">{{ record.t5 }}</td>
              <td class="ts-whitespace-no-wrap text-center">{{ record.t6 }}</td>
              <td class="ts-whitespace-no-wrap text-center">
                {{ record.total_hour3 }}
              </td>
              <td class="ts-whitespace-no-wrap text-center">{{ record.t7 }}</td>
              <td class="ts-whitespace-no-wrap text-center">{{ record.t8 }}</td>
              <td class="ts-whitespace-no-wrap text-center">
                {{ record.total_hour4 }}
              </td>
              <td class="ts-whitespace-no-wrap text-center">
                <a
                  v-if="record.scan_status == 'Manual' || record.t2 == null"
                  href="#"
                  class="mr-2 text-primary"
                  @click.prevent="edit(record)"
                  v-tooltip="$t('edit')"
                >
                  <i class="fas fa-edit"></i>
                </a>
              </td>
            </template>
          </ts-table>
        </div>
        <div class="d-flex justify-content-between align-items-center p-15">
          <ts-total-list v-model="pagination" />
          <ts-pagination
            v-model="pagination"
            @navigate="fetchData"
          ></ts-pagination>
        </div>
        <a-drawer
          :title="$t('filter')"
          placement="right"
          :closable="false"
          :visible="visible"
          @close="() => (visible = false)"
          :zIndex="1020"
          width="300px"
        >
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <ts-branch-filter @filter="value => (branch_id = value)" />
              </div>
            </div>
          </div>
        </a-drawer>
      </div>
    </ts-card>
  </div>
</template>

<script>
import { LOAD_ROOT_OPTIONS } from '@riophae/vue-treeselect'
import TableProgressBar from '@/components/common/TsTable/TableProgressBar'
import { Errors } from 'form-backend-validation'
import { mapState, mapActions } from 'vuex'
import moment from 'moment'
import { debounce, trim } from 'lodash'
import * as FileDownload from 'downloadjs'
import FormComponent from './form'
import EditComponent from './edit'

export default {
  name: 'campusAttendance',
  components: { TableProgressBar },
  data () {
    return {
      loading: false,
      exporting: false,
      model: {
        date_from: moment().startOf('month').format('DD-MM-YYYY'),
        date_to: moment().endOf('month').format('DD-MM-YYYY'),
        class_id: null,
        student_id: []
      },
      branch_id: [],
      visible: false,
      studentOptions: null,
      classOptions: null,
      errors: new Errors()
    }
  },
  computed: {
    ...mapState('campusAttendance', ['resources', 'pagination'])
  },
  methods: {
    ...mapActions('campusAttendance', ['fetchClasses', 'fetchStudents']),
    async loadClasses ({ action /*, callback*/ }) {
      if (action === LOAD_ROOT_OPTIONS) {
        this.classOptions = null
        let response = await this.fetchClasses()
        this.classOptions = response.data.map(el => ({
          id: el.class_id,
          label:
            el.class_name +
            ' | ' +
            el.academic_study_time.study_time +
            ' | ' +
            el.academic_study_time.session +
            ' | ' +
            el.teacher.full_name_en
        }))
      }
    },
    async loadStudent ({ action /*, callback*/ }) {
      if (action === LOAD_ROOT_OPTIONS) {
        this.studentOptions = null
        await this.fetchStudents({
          class_id: this.model.class_id ? this.model.class_id : null
        })
          .then(response => {
            this.studentOptions = response.data.map(el => ({
              id: el.student_id,
              label: el.full_name_en + '|' + el.full_name_kh
            }))
          })
          .catch(error => {
            this.$toasted.error(error.message)
          })
      }
    },
    fetchData (attributes) {
      this.loading = true
      this.errors = new Errors()
      this.$store
        .dispatch(
          'campusAttendance/fetch',
          Object.assign(
            {},
            this.model,
            { branch_id: this.branch_id },
            attributes
          )
        )
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
    },
    exportExcel () {
      this.exporting = true
      this.errors = new Errors()

      this.$store
        .dispatch('campusAttendance/exportExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
    showForm (data) {
      this.$modal.show(
        FormComponent,
        {
          data: data
        },
        {
          height: 'auto',
          width: '580px',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        },
        {
          'before-close': this.fetchData
        }
      )
    },
    edit (data) {
      this.$modal.show(
        EditComponent,
        {
          editData: data
        },
        {
          height: 'auto',
          width: '580px',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        },
        {
          'before-close': this.fetchData
        }
      )
    }
  },
  watch: {
    'model.class_id': function (value) {
      this.model.student_id = []
      this.studentOptions = null
      if (value) {
        this.studentOptions = this.fetchStudents({ class_id: value })
          .then(response => {
            this.studentOptions = response.data.map(el => ({
              id: el.student_id,
              label: el.full_name_en + '|' + el.full_name_kh
            }))
            response.data.forEach(el => {
              this.model.student_id.push(el.student_id)
            })
          })
          .catch(error => {
            this.$toasted.error(error.message)
          })
      }
    },
    branch_id: debounce(function (value) {
      this.fetchData({ branch_id: value })
    }, 500)
  }
}
</script>

<style lang="scss" scoped>
#table-container {
  min-height: 60vh;
  overflow-y: scroll;
}

table > tbody > tr td {
  vertical-align: middle;
}

.table > thead > tr > th {
  border: 1px solid #b6c2c9;
  vertical-align: middle;
}
::-webkit-scrollbar {
  height: 8px;
  width: 3px; /* Remove scrollbar space */
  background: #ffff; /* Optional: just make scrollbar invisible */
}

/* Optional: show position indicator in red */
::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: #e5e7e7;
}
div::-webkit-scrollbar-thumb:hover {
  background: #b7b7b8;
}
</style>
